import styled from "styled-components";
import { useState } from "react";
import { ImmediateActionCard } from "../types/ask";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { ItemCard } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCard/ItemCard";
import { ItemCardWithCopyNo } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithCopyNo/ItemCardWithCopyNo";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import ArrowRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ArrowRightIconPath";
import { ItemBadge } from "@coworker/apprestructured/src/shared/types/ItemBadge";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { LoadMore } from "@coworker/apprestructured/src/shared/components/LoadMore/LoadMore";
import { useTranslation } from "@coworker/locales";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10;

function groupCardsByBadge(
  cards: ImmediateActionCard[]
): Map<string, ImmediateActionCard[]> {
  const map = new Map<string, ImmediateActionCard[]>();
  for (const card of cards) {
    if (!map.has(card.badge)) map.set(card.badge, []);
    map.get(card.badge)!.push(card);
  }
  return map;
}

function cardBadge(badge: ImmediateActionCard["badge"]): ItemBadge {
  switch (badge) {
    case "sale_stop":
      return { text: "Sale stop", colour: "red" };
    case "past_eds":
      return { text: "Past EDS", colour: "orange" };
    default:
      return { text: badge, colour: "grey" };
  }
}

// ---------------------------------------------------------------------------
// Styled components — only for the below-the-fold section
// ---------------------------------------------------------------------------

const GroupWrapper = styled.div`
  margin-bottom: 12px;
`;

const InlineMessageWrapper = styled.div`
  margin-bottom: 8px;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProductCardBox = styled.div`
  background-color: var(--white);
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  border: 0 solid var(--grey200);
  overflow: hidden;
`;

const ProductRowWrapper = styled.div`
  padding: 12px;
  background: var(--white);
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: var(--grey900);
  line-height: 1.5;
  margin: 12px 0 25px;
`;

const MediaFoundHeading = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--black);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const MediaTable = styled.div`
  width: 100%;
  margin-bottom: 25px;
`;

const MediaTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 32px;
  padding: 6px 0;
  border-bottom: 1px solid var(--grey300);
  font-size: 12px;
  font-weight: 700;
  color: var(--grey600);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const MediaTableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 32px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--grey100);
  font-size: 14px;
  color: var(--grey900);
  cursor: pointer;
  &:last-child {
    border-bottom: none;
  }
`;

const MediaTableCell = styled.div`
  line-height: 1.4;
  font-size: 14px;
  font-weight: 700;
  min-width: 0;
  overflow-wrap: break-word;
`;

const ChevronCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

// ---------------------------------------------------------------------------
// Single product: card + description + media table as one unit
// ---------------------------------------------------------------------------

function ProductCardWithDetails({
  card,
  onCopy,
}: {
  card: ImmediateActionCard;
  onCopy: () => void;
}) {
  const { data: itemInfo } = useItemInfo(card.product_id);
  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const mediaRows = card.media_list ?? [];

  const name = itemInfo?.name ?? card.item_name;
  const formattedId = itemInfo?.noFormatted ?? card.product_id;

  return (
    <>
      <ProductCardBox>
        <ProductRowWrapper>
          <ItemCard badges={[cardBadge(card.badge)]}>
            {itemInfo && <ItemCardWithCopyNo item={itemInfo} onCopy={onCopy} />}
          </ItemCard>
        </ProductRowWrapper>
      </ProductCardBox>

      {card.description && (
        <CardDescription>{card.description}</CardDescription>
      )}

      <MediaFoundHeading>
        {name} {formattedId} is found in following media
      </MediaFoundHeading>

      {mediaRows.length > 0 && (
        <MediaTable>
          <MediaTableHeader>
            <MediaTableCell>{t("mediaNameString")}</MediaTableCell>
            <MediaTableCell>{t("location3String")}</MediaTableCell>
            <MediaTableCell>{t("askFixaQuantityString")}</MediaTableCell>
            <MediaTableCell />
          </MediaTableHeader>
          {mediaRows.map((row, i) => (
            <MediaTableRow
              key={`${row.media_name}-${i}`}
              onClick={() => {
                if (row.media_id) {
                  navigate(`/mediamaintenance/tabs/${row.media_id}`);
                }
              }}
            >
              <MediaTableCell>{row.media_name}</MediaTableCell>
              <MediaTableCell>{row.media_location ?? "—"}</MediaTableCell>
              <MediaTableCell>
                {row.item_count} {row.item_count === 1 ? "piece" : "pieces"}
              </MediaTableCell>
              <ChevronCell>
                <FixaSSRIcon paths={ArrowRightIconPath} />
              </ChevronCell>
            </MediaTableRow>
          ))}
        </MediaTable>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Group: one FixaInlineMessage header + each product rendered with its details
// ---------------------------------------------------------------------------

function ImmediateActionGroup({
  cards,
  onCopy,
}: {
  cards: ImmediateActionCard[];
  onCopy: () => void;
}) {
  const firstCard = cards[0]!;
  const isNegative = firstCard.badge === "sale_stop";
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  return (
    <GroupWrapper>
      <InlineMessageWrapper>
        <FixaInlineMessage
          variant={isNegative ? "negative" : "cautionary"}
          title={firstCard.title}
          body={firstCard.subtitle}
        />
      </InlineMessageWrapper>

      <ProductList>
        {cards.slice(0, visibleCount).map((card, i) => (
          <ProductCardWithDetails
            key={`${card.product_id}-${card.media_name}-${i}`}
            card={card}
            onCopy={onCopy}
          />
        ))}
      </ProductList>
      {visibleCount < cards.length && (
        <LoadMore
          totalCount={cards.length}
          loadedItemsCount={Math.min(visibleCount, cards.length)}
          onLoadMoreClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
        />
      )}
    </GroupWrapper>
  );
}

// ---------------------------------------------------------------------------
// Public export
// ---------------------------------------------------------------------------

interface Props {
  cards: ImmediateActionCard[];
  onCopy: () => void;
}

export function ImmediateActionCards({ cards, onCopy }: Props) {
  const immediateCards = cards.filter(
    (c) => c.card_type === "IMMEDIATE_ACTION"
  );
  if (immediateCards.length === 0) return null;

  const groups = groupCardsByBadge(immediateCards);

  return (
    <>
      {Array.from(groups.entries()).map(([badge, groupCards]) => (
        <ImmediateActionGroup key={badge} cards={groupCards} onCopy={onCopy} />
      ))}
    </>
  );
}
